import { alpha } from '@mui/material';
import { palette } from '@client/theme/colors';
import { radii, theme } from '@client/theme/constants';
import { shadows } from '@client/theme/shadows';
import { typography } from '@client/theme/typography';
import type { ThemeOptions } from '@mui/material';
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';

const extraComponents: any = {
  MuiDateCalendar: {
    styleOverrides: {
      root: {
        padding: theme.spacing(2),
        maxHeight: 368,
      },
    },
  },
  MuiDayCalendar: {
    styleOverrides: {
      header: {
        justifyContent: 'space-between',
      },
      slideTransition: {
        minHeight: 290,
      },
      weekContainer: {
        margin: 0,
        justifyContent: 'space-between',
      },
      weekDayLabel: {
        height: 33,
        width: 33,
        margin: '5px 0px',
      },
    },
  },
};

const Dialog: ThemeOptions['components'] = {
  MuiClock: {
    styleOverrides: {
      amButton: {
        '& > span': {
          ...typography.body1,
        },
      },
      pmButton: {
        '& > span': {
          ...typography.body1,
        },
      },
      clock: {
        backgroundColor: palette.grey[200],
      },
    },
  },
  MuiClockNumber: {
    styleOverrides: {
      root: {
        ...typography.subtitle1,
        color: palette.grey[600],

        '&.Mui-selected': {
          color: palette.grey[50],
        },
      },
    },
  },
  ...extraComponents,
  MuiPickersPopper: {
    styleOverrides: {
      paper: {
        borderRadius: radii.large,
        boxShadow: shadows[4],
      },
    },
  },
  MuiPickersCalendarHeader: {
    styleOverrides: {
      root: {
        margin: 0,
        padding: 0,
      },
      label: {
        fontWeight: 600,
      },
    },
  },
  MuiPickersArrowSwitcher: {
    styleOverrides: {
      button: {
        '&:last-of-type': {
          paddingRight: 0,
        },
      },
      spacer: {
        width: theme.spacing(1),
      },
    },
  },
  MuiPickersMonth: {
    styleOverrides: {
      monthButton: {
        backgroundColor: palette.grey[200],
        ...typography.body1,

        '&.Mui-selected': {
          color: palette.grey[50],

          '&:focus': {
            backgroundColor: palette.primary.main,
          },
        },
      },
    },
  },
  MuiPickersYear: {
    styleOverrides: {
      yearButton: {
        backgroundColor: palette.grey[200],
        ...typography.body1,

        '&.Mui-selected': {
          color: palette.grey[50],

          '&:focus': {
            backgroundColor: palette.primary.main,
          },
        },
      },
    },
  },
  MuiPickersDay: {
    styleOverrides: {
      root: {
        backgroundColor: palette.grey[200],
        margin: '5px 3.5px',
        height: 33,
        width: 33,
        ...typography.body1,

        '&.Mui-selected': {
          color: palette.grey[50],

          '&:focus,&:hover': {
            backgroundColor: palette.primary.main,
          },
        },
      },
    },
  },
  MuiPickersLayout: {
    styleOverrides: {
      root: {
        '& > .MuiDialogActions-root': {
          // Custom class we're adding to the action bar to ensure it's hidden on mobile
          '&.MuiDateRangePicker-actionBarHidden': {
            display: 'none',
          },

          [theme.breakpoints.up('md')]: {
            display: 'none',
          },

          padding: theme.spacing(2),
          margin: '0 auto',
          width: 320,

          // cancel button - replicate outlined primary button
          'button:first-of-type': {
            backgroundColor: palette.grey[50],
            borderColor: palette.primary.main,
            borderStyle: 'solid',
            borderWidth: 2,
            color: palette.primary.main,
            height: 40,
            padding: '0px 14px',

            '&:hover': {
              backgroundColor: palette.grey[50],
              borderColor: palette.primary.dark,
              borderStyle: 'solid',
              borderWidth: 2,
              color: palette.primary.dark,
            },
          },

          // save {time,date} button - replicate contained secondary button
          'button:last-of-type': {
            marginRight: theme.spacing(1),

            backgroundColor: palette.primary.main,
            color: palette.grey[50],
            padding: theme.spacing(1, 1 + 1 / 2),
            height: 40,
            borderRadius: radii.small,
            fontSize: typography.button.fontSize,
            lineHeight: typography.button.lineHeight,
            fontWeight: 600,

            '&:focus, &:active': {
              color: alpha(palette.grey[50], 0.8),
            },

            '&:active': {
              boxShadow: shadows[2],
            },

            '&:hover': {
              backgroundColor: palette.primary.light,
            },

            '&:disabled': {
              backgroundColor: palette.primary.light,
            },
          },
        },
      },
    },
  },
  MuiPickersToolbar: {
    styleOverrides: {
      root: {
        [theme.breakpoints.up('md')]: {
          display: 'none',
        },

        '& > span': {
          display: 'none',
        },
      },
      content: {
        '& button': {
          height: 'auto',
        },
      },
    },
  },
  MuiDateRangeCalendar: {
    styleOverrides: {
      root: {
        '& .MuiDayCalendar-slideTransition': {
          minHeight: 234,
        },
      },
      monthContainer: {
        borderColor: palette.grey[300],
        borderRadius: radii.large,
        borderStyle: 'solid',
        borderWidth: 1,
        padding: theme.spacing(2),
        width: '100%',
        maxWidth: 335,
        minWidth: 320,

        '& .MuiPickersSlideTransition-root': {
          minWidth: 'auto',
        },
      },
    },
  },
  MuiDateRangePickerDay: {
    styleOverrides: {
      day: {
        borderWidth: '0px !important',
        margin: '3px 0px',
        transform: 'scale(1)',
      },
      rangeIntervalPreview: {
        borderWidth: 0,
      },
      rangeIntervalDayHighlight: {
        backgroundColor: 'transparent',
        borderWidth: 0,

        '& button': {
          backgroundColor: palette.primary.light,
          color: `${palette.grey[50]} !important`,
        },

        '&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart, &.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd': {
          '& > :not(.MuiDateRangePickerDay-rangeIntervalDayPreviewEnd), & > :not(.MuiDateRangePickerDay-rangeIntervalDayPreviewStart)': {
            '& > button': {
              backgroundColor: palette.primary.main,
              color: `${palette.grey[50]} !important`,
            },
          },

          '& > .MuiDateRangePickerDay-rangeIntervalDayPreviewEnd, & > .MuiDateRangePickerDay-rangeIntervalDayPreviewStart': {
            '& > button': {
              backgroundColor: palette.primary.light,
              color: `${palette.grey[50]} !important`,
            },

            '& > button.Mui-selected': {
              backgroundColor: palette.primary.main,
            },
          },
        },
      },
      rangeIntervalDayPreview: {
        '& button': {
          backgroundColor: palette.primary.light,
          color: `${palette.grey[50]} !important`,
        },

        '&.MuiDateRangePickerDay-rangeIntervalDayPreviewStart button, &.MuiDateRangePickerDay-rangeIntervalDayPreviewEnd button': {
          backgroundColor: palette.primary.main,
          color: `${palette.grey[50]} !important`,
        },
      },
      notSelectedDate: {
        '&:hover': {
          outlineWidth: '1.5px',
          outlineStyle: 'solid',
          outlineColor: palette.primary.light,
        },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        minWidth: 365,
        borderRadius: radii.large,

        [theme.breakpoints.down('md')]: {
          margin: 0,
          width: '100%',
          minWidth: 'auto',
          borderRadius: 0,
        },
      },
      paperFullScreen: {
        borderRadius: 0,
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        margin: 0,
        paddingBottom: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        paddingTop: theme.spacing(0),

        [theme.breakpoints.up('md')]: {
          paddingBottom: theme.spacing(3),
          paddingLeft: theme.spacing(3),
          paddingRight: theme.spacing(3),
        },
      },
      spacing: {
        flexDirection: 'row-reverse',

        '& > button:not(:first-of-type)': {
          marginRight: theme.spacing(2),
          marginLeft: 0,
        },
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: theme.spacing(2),

        [theme.breakpoints.down('md')]: {
          flex: 'none',
        },
      },
    },
  },
  MuiDialogContentText: {
    styleOverrides: {
      root: {
        marginBottom: 0,
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        marginBottom: theme.spacing(1),
        position: 'relative',
      },
    },
  },
};

export default Dialog;
